export const VARIABLE = 'variable';
export const SIGN = 'sign';

type EquationSign = '+' | '-' | '=';
type Row =
  | {
      text: string;
      value?: never;
    }
  | {
      text?: never;
      value: string;
    };

type EquationProps =
  | {
      type: 'variable';
      rows: Row[];
      value?: never;
    }
  | {
      type: 'sign';
      rows?: never;
      value: EquationSign;
    };

export interface RCDetailsTableAccordion {
  /**
   * Classname override
   */
  className?: string;
  /**
   * Details to render the equation with in each accordion.
   */
  contentConfig: EquationProps[];
  /**
   * Accordion item section title.
   */
  header: string;
  /**
   * Value to show in accordion title.
   */
  headerValue: string;
  /**
   * Helpbubble content.
   */
  help: string;
  /**
   * Used as the entity name or section name for analytics tracking.
   */
  trackingFieldName: string;
}

const DetailsTableAccordionConfig: RCDetailsTableAccordion[] = [
  {
    header: 'Cash savings',
    trackingFieldName: 'cash_savings',
    className: 'cashSavings',
    headerValue: 'details.savings.cumulativeTotal',
    help: `How much less your payments (plus closing costs) will be during the selected number of years. If this number is negative, it means your payment will have increased.`,
    contentConfig: [
      {
        type: VARIABLE,
        rows: [
          { text: '' },
          { text: 'Closing costs' },
          { text: 'Principal' },
          { text: 'Interest' },
          { text: 'Cash savings' },
        ],
      },
      {
        type: VARIABLE,
        rows: [
          { text: 'Original loan' },
          { text: '' },
          { value: 'details.currentLoan.cumulativePrincipal' },
          { value: 'details.currentLoan.cumulativeInterestPaid' },
          { value: 'details.currentLoan.cumulativeTotal' },
        ],
      },
      {
        type: SIGN,
        value: '-',
      },
      {
        type: VARIABLE,
        rows: [
          { text: 'New loan' },
          { value: 'details.refinanceLoan.closingCosts' },
          { value: 'details.refinanceLoan.cumulativePrincipal' },
          { value: 'details.refinanceLoan.cumulativeInterestPaid' },
          { value: 'details.savings.cumulativeTotal' },
        ],
      },
      {
        type: SIGN,
        value: '=',
      },
      {
        type: VARIABLE,
        rows: [
          { text: '' },
          { text: '' },
          { text: '' },
          { text: '' },
          { value: 'details.savings.cumulativeTotal' },
        ],
      },
    ],
  },
  {
    header: 'Difference in principal paid',
    trackingFieldName: 'equity_difference',
    headerValue: 'details.savings.cumulativePrincipal',
    help: 'The difference in principal paid up to this point on your new loan compared to principal you would have paid on your old loan.',
    contentConfig: [
      {
        type: VARIABLE,
        rows: [
          { text: 'Original principal paid to this point' },
          { value: 'details.currentLoan.cumulativePrincipal' },
        ],
      },
      {
        type: SIGN,
        value: '-',
      },
      {
        type: VARIABLE,
        rows: [
          { text: 'New principal paid to this point' },
          { value: 'details.refinanceLoan.cumulativePrincipal' },
        ],
      },
      {
        type: SIGN,
        value: '=',
      },
      {
        type: VARIABLE,
        rows: [
          { text: 'Difference in principal paid' },
          { value: 'details.savings.cumulativePrincipal' },
        ],
      },
    ],
  },
  {
    header: 'Total savings',
    trackingFieldName: 'total_savings',
    headerValue: 'details.savings.totalSavings',
    help: `How much less you would pay over the selected number of years by refinancing.`,
    contentConfig: [
      {
        type: VARIABLE,
        rows: [
          { text: 'Cash savings' },
          { value: 'details.savings.cumulativeTotal' },
        ],
      },
      {
        type: SIGN,
        value: '+',
      },
      {
        type: VARIABLE,
        rows: [
          { text: 'Difference in equity' },
          { value: 'details.savings.cumulativePrincipal' },
        ],
      },
      {
        type: SIGN,
        value: '=',
      },
      {
        type: VARIABLE,
        rows: [
          { text: 'Total savings' },
          { value: 'details.savings.totalSavings' },
        ],
      },
    ],
  },
];

export default DetailsTableAccordionConfig;
